import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { chatApi } from '../../api/chat';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useKeyboardOffset } from '../../hooks/useKeyboardOffset';
import './WorkshopChat.css';

/**
 * Detect if message contains a sequence generation intent
 * @param {string} message - User's message
 * @returns {{ type: string, actNumber: number } | null}
 */
function detectSequenceGenerationIntent(message) {
  const patterns = [
    /generate\s+sequences?\s+for\s+act\s+(\d+)/i,
    /help\s+(?:me\s+)?develop\s+act\s+(\d+)/i,
    /sequences?\s+for\s+act\s+(\d+)/i,
    /break\s+(?:down\s+)?act\s+(\d+)/i,
    /develop\s+sequences?\s+(?:for\s+)?act\s+(\d+)/i,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) {
      return {
        type: 'generate_sequences',
        actNumber: parseInt(match[1], 10)
      };
    }
  }

  return null;
}

/**
 * Detect structure generation intents for the Structure Workshop
 * Supports acts, sequences, scenes, and beats
 * @param {string} message - User's message
 * @returns {{ type: string, target: string, parentRef?: string } | null}
 */
function detectStructureGenerationIntent(message) {
  const lowerMessage = message.toLowerCase();

  // Act generation patterns
  const actPatterns = [
    /generate\s+acts?/i,
    /create\s+(?:my\s+)?acts?/i,
    /help\s+(?:me\s+)?(?:with\s+)?(?:the\s+)?acts?/i,
    /suggest\s+acts?/i,
    /what\s+(?:should\s+)?(?:my\s+)?acts?\s+be/i,
  ];

  for (const pattern of actPatterns) {
    if (pattern.test(message)) {
      return { type: 'generate', target: 'act' };
    }
  }

  // Sequence generation patterns (with or without parent reference)
  const sequencePatterns = [
    /generate\s+sequences?(?:\s+for\s+this)?/i,
    /break\s+(?:this\s+)?(?:down|up)\s+(?:into\s+)?sequences?/i,
    /suggest\s+sequences?/i,
    /help\s+(?:me\s+)?develop\s+(?:this\s+)?(?:into\s+)?sequences?/i,
    /what\s+sequences?\s+(?:should\s+)?(?:I\s+)?(?:have|use)/i,
  ];

  for (const pattern of sequencePatterns) {
    if (pattern.test(message)) {
      return { type: 'generate', target: 'sequence' };
    }
  }

  // Scene generation patterns
  const scenePatterns = [
    /generate\s+scenes?(?:\s+for\s+this)?/i,
    /break\s+(?:this\s+)?(?:down|up)\s+(?:into\s+)?scenes?/i,
    /suggest\s+scenes?/i,
    /help\s+(?:me\s+)?develop\s+(?:this\s+)?(?:into\s+)?scenes?/i,
    /what\s+scenes?\s+(?:should\s+)?(?:I\s+)?(?:have|use)/i,
  ];

  for (const pattern of scenePatterns) {
    if (pattern.test(message)) {
      return { type: 'generate', target: 'scene' };
    }
  }

  // Beat generation patterns
  const beatPatterns = [
    /generate\s+beats?(?:\s+for\s+this)?/i,
    /break\s+(?:this\s+)?(?:down|up)\s+(?:into\s+)?beats?/i,
    /suggest\s+beats?/i,
    /help\s+(?:me\s+)?develop\s+(?:this\s+)?(?:into\s+)?beats?/i,
    /what\s+beats?\s+(?:should\s+)?(?:I\s+)?(?:have|use)/i,
    /moment\s+by\s+moment/i,
  ];

  for (const pattern of beatPatterns) {
    if (pattern.test(message)) {
      return { type: 'generate', target: 'beat' };
    }
  }

  return null;
}

/**
 * WorkshopChat - Chat interface for workshop session
 *
 * Props:
 * - sessionId: UUID of the workshop session
 * - storyId: UUID of the story
 * - zoom: number (80-250) - text zoom percentage (Theme Workshop stores this; it does not render a stepper)
 * - template: string - workshop template type (e.g., 'workshop_sequence')
 * - onGenerateRequest: (actNumber: number) => void - callback for sequence generation
 * - collapsed: boolean - whether chat is collapsed
 * - onToggleCollapse: () => void - callback to toggle collapse state
 * - messagePrefix: string - optional prefix to prepend to outgoing messages (for context injection)
 * - suggestionSlot: ReactNode - optional slot for rendering suggestion cards above the input
 */
export default function WorkshopChat({
  sessionId,
  storyId,
  zoom = 100,
  template,
  onGenerateRequest,
  collapsed = false,
  onToggleCollapse,
  messagePrefix = '',
  suggestionSlot
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const breakpoint = useBreakpoint();
  useKeyboardOffset();

  useEffect(() => {
    if (sessionId) {
      fetchMessages();
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await chatApi.getMessages(sessionId);
      // Handle various response formats: { data: [...] }, { data: { messages: [...] } }, or direct array
      let messagesData = response?.data || response;
      if (messagesData?.messages) {
        messagesData = messagesData.messages;
      }
      setMessages(Array.isArray(messagesData) ? messagesData : []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userInput = input.trim();
    // Prepend messagePrefix if provided (for context injection)
    const content = messagePrefix ? `${messagePrefix}${userInput}` : userInput;

    // Check for sequence generation intent in Sequences Workshop
    if (template === 'workshop_sequence' && onGenerateRequest) {
      const intent = detectSequenceGenerationIntent(content);
      if (intent?.type === 'generate_sequences') {
        // Trigger generation via parent component
        onGenerateRequest(intent.actNumber);
        // Still send the message to chat for context
      }
    }

    // Check for structure generation intent in Structure Workshop
    if (template === 'workshop_structure' && onGenerateRequest) {
      const intent = detectStructureGenerationIntent(content);
      if (intent?.type === 'generate') {
        // Trigger generation via parent component with target type
        onGenerateRequest(intent.target);
        // Still send the message to chat for context
      }
    }

    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      speaker: 'user',
      content: userInput, // Display only user's input, not the prefix
      created_at: new Date().toISOString()
    };
    const streamingId = `streaming-${Date.now()}`;
    const streamingMessage = {
      id: streamingId,
      role: 'assistant',
      speaker: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
      streaming: true
    };

    setMessages(prev => [...prev, userMessage, streamingMessage]);
    setInput('');
    setSending(true);

    try {
      const data = await chatApi.sendMessageStream(sessionId, content, null, null, {
        onDelta: (text) => {
          setMessages(prev => prev.map(m => (
            m.id === streamingId ? { ...m, content: `${m.content}${text}` } : m
          )));
        }
      });

      setMessages(prev => {
        const withoutTemps = prev.filter(m => m.id !== userMessage.id && m.id !== streamingId);
        const incoming = [];
        if (data.user_message) incoming.push({ ...data.user_message, role: data.user_message.speaker });
        if (data.assistant_message) {
          incoming.push({
            ...data.assistant_message,
            role: data.assistant_message.speaker,
            interrupted: !!data.interrupted,
          });
        }
        return [...withoutTemps, ...incoming];
      });
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages(prev => prev.filter(m => m.id !== userMessage.id && m.id !== streamingId));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (breakpoint === 'mobile') return;
      if (!e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    }
  };

  return (
    <div className="workshop-chat" style={{ '--zoom-scale': zoom / 100 }}>
      <div className="workshop-chat-messages">
        {loading ? (
          <div className="workshop-chat-loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="workshop-chat-empty">
            <p>Start the conversation! Ask questions or discuss ideas.</p>
          </div>
        ) : (
          messages.map(message => (
            <div
              key={message.id}
              className={`workshop-chat-message ${message.role || message.speaker || ''} ${message.streaming ? 'streaming' : ''}`}
            >
              <div className="workshop-chat-message-content">
                {message.content}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestion slot (e.g., for AI-generated suggestions) */}
      {suggestionSlot}

      <div className="workshop-chat-footer">
        <div className="workshop-chat-input-container">
          <textarea
            className="workshop-chat-input"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            rows={2}
          />
          <button
            className="workshop-chat-send"
            onClick={sendMessage}
            disabled={!input.trim() || sending}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export { WorkshopChat };
